import React, { useState, useEffect } from 'react';
import { createInquiry } from '../../api/inquiries';
import { getCategories } from '../../api/cases';
import './InquiryModal.css';

const InquiryModal = ({ isOpen, onClose, type = 'PROPOSAL', inline = false }) => {
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [form, setForm] = useState({
        companyName: '',
        customerName: '',
        email: '',
        phone: '',
        message: '',
        privacyAgreed: false,
        showPrivacy: false
    });
    const [status, setStatus] = useState('idle'); // idle, submitting, success, error

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await getCategories();
                // data is expected to be array of objects: { id, code, label, ... }
                // We'll use 'code' or 'id' as the value. Let's use 'code' based on typical usage, or 'id' if that's what UseCases uses.
                // In UseCases.jsx: key={cat.id} ... onClick={() => setActiveCategory(cat)}
                setCategories(data);
            } catch (error) {
                console.error("Failed to load categories:", error);
                // Fallback or empty? User requested dynamic, so maybe leave empty or minimal fallback
                setCategories([]);
            }
        };
        fetchCategories();
    }, []);

    // Reset when modal opens (optional, but good for UX)
    // For inline, we might not want to reset unexpectedly, but component mounts once.

    // Modal Text Logic
    let title = '표준 제안서 받기';
    let desc = '이메일 주소를 입력해주시면\n제안서(PDF)가 즉시 발송됩니다.';

    if (type === 'DEMO') {
        title = '솔루션 데모 신청';
        desc = '관심있는 솔루션을 선택해주시면\n담당자가 확인 후 체험 계정을 발급해드립니다.';
    } else if (type === 'SUPPORT') {
        title = '기술지원 문의';
        desc = '사용 중 발생한 문제나 궁금한 점을\n상세히 남겨주시면 빠르게 답변드리겠습니다.';
    } else if (type === 'CONTACT') {
        title = '상담/미팅 요청';
        desc = '궁금하신 점을 남겨주시면\n전문 컨설턴트가 자세히 상담해 드립니다.';
    } else if (type === 'ESTIMATE') {
        title = '예상 견적 요청';
        desc = '원하시는 구성이나 환경을 알려주시면\n대략적인 견적을 안내해 드립니다.';
    } else if (type === 'PROPOSAL') {
        title = '표준 제안서 받기';
        desc = '이메일 주소를 입력해주시면\n제안서(PDF)가 즉시 발송됩니다.';
    }

    const toggleCategory = (code) => {
        if (selectedCategories.includes(code)) {
            setSelectedCategories(selectedCategories.filter(c => c !== code));
        } else {
            setSelectedCategories([...selectedCategories, code]);
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.companyName || !form.customerName || !form.email || !form.message) {
            alert('필수 항목을 모두 입력해주세요.\n(회사명, 담당자명, 이메일, 내용)');
            return;
        }

        if (!form.privacyAgreed) {
            alert('개인정보 수집 및 이용에 동의해주세요.');
            return;
        }

        setStatus('submitting');
        try {
            await createInquiry({
                type: type,
                ...form,
                contactInfo: form.phone,
                targetServices: selectedCategories, // Arrays of codes
                status: 'UNREAD'
            });

            setStatus('success');
            alert('문의가 접수되었습니다. 담당자가 확인 후 연락드리겠습니다.');

            setForm({ companyName: '', customerName: '', email: '', phone: '', message: '' });
            setSelectedCategories([]);
            setStatus('idle');

            if (onClose) onClose();
        } catch (error) {
            console.error(error);
            alert('문의 전송에 실패했습니다. 잠시 후 다시 시도해주세요.');
            setStatus('error');
        }
    };

    const formContent = (
        <form className="unified-inquiry-form" onSubmit={handleSubmit}>
            {/* Step 1: Categories */}
            <div className="form-section">
                <label className="modal-section-title">관심 희망 분야 (중복 선택 가능)</label>
                <div className="capsule-container">
                    {categories.length > 0 ? (
                        categories.map(cat => (
                            <button
                                key={cat.id}
                                type="button"
                                className={`capsule-btn ${selectedCategories.includes(cat.code) ? 'active' : ''}`}
                                onClick={() => toggleCategory(cat.code)}
                            >
                                {cat.label}
                            </button>
                        ))
                    ) : (
                        <p style={{ color: '#999', fontSize: '0.9rem' }}>카테고리 정보를 불러오는 중...</p>
                    )}
                </div>
            </div>

            {/* Step 2: Information */}
            <div className="form-section">
                <label className="modal-section-title">상세 정보</label>
                <div className="form-grid">
                    <input
                        type="text"
                        name="companyName"
                        placeholder="회사/기관명 (필수)"
                        className="input-field"
                        value={form.companyName}
                        onChange={handleChange}
                        required
                    />
                    <input
                        type="text"
                        name="customerName"
                        placeholder="담당자명 (필수)"
                        className="input-field"
                        value={form.customerName}
                        onChange={handleChange}
                        required
                    />
                    <input
                        type="email"
                        name="email"
                        placeholder="이메일 주소 (필수)"
                        className="input-field"
                        value={form.email}
                        onChange={handleChange}
                        required
                    />
                    <input
                        type="tel"
                        name="phone"
                        placeholder="연락처 (선택)"
                        className="input-field"
                        value={form.phone}
                        onChange={handleChange}
                    />
                    <textarea
                        name="message"
                        placeholder="문의하실 내용을 자유롭게 적어주세요. (필수)"
                        className="input-field full-width"
                        value={form.message}
                        onChange={handleChange}
                        required
                    ></textarea>
                </div>
            </div>

            {/* Privacy Agreement */}
            <div className="terms-agreement" style={{ marginTop: '1rem', marginBottom: '1rem', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input
                        type="checkbox"
                        checked={form.privacyAgreed}
                        onChange={(e) => setForm({ ...form, privacyAgreed: e.target.checked })}
                        style={{ marginRight: '8px' }}
                    />
                    <span>개인정보 수집 및 이용 동의 <strong style={{ color: '#e65100' }}>[필수]</strong></span>
                </label>
                <button
                    type="button"
                    onClick={() => setForm({ ...form, showPrivacy: !form.showPrivacy })}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#666',
                        textDecoration: 'underline',
                        fontSize: '0.8rem',
                        marginLeft: 'auto',
                        cursor: 'pointer',
                        padding: '0 5px'
                    }}
                >
                    {form.showPrivacy ? '접기' : '상세보기'}
                </button>

                {/* Inline Privacy Details */}
                {form.showPrivacy && (
                    <div style={{
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#fff',
                        border: '1px solid #eee',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        color: '#555',
                        lineHeight: '1.5'
                    }}>
                        <p style={{ margin: '0 0 5px 0' }}><strong>1. 수집항목:</strong> 회사/기관명, 담당자명, 이메일, 연락처</p>
                        <p style={{ margin: '0 0 5px 0' }}><strong>2. 수집목적:</strong> 상담 문의 처리 및 결과 회신, 견적서 발송</p>
                        <p style={{ margin: '0 0 0 0' }}><strong>3. 보유기간:</strong> 3년 (상담 이력 관리)</p>
                    </div>
                )}
            </div>

            <button type="submit" className="submit-btn" disabled={status === 'submitting'}>
                {status === 'submitting' ? '전송 중...' : '문의 접수하기'}
            </button>
            <p className="form-note">
                입력하신 정보는 문의 처리를 위해서만 사용되며, 안전하게 보호됩니다.
            </p>
        </form>
    );

    if (inline) {
        return formContent;
    }

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content inquiry-modal-box" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>&times;</button>

                <h2 className="modal-title">{title}</h2>
                <p className="modal-desc" style={{ whiteSpace: 'pre-line' }}>{desc}</p>

                {formContent}
            </div>
        </div>
    );
};

export default InquiryModal;
